import { Box, HStack, Text, Image, Flex, Divider } from '@chakra-ui/react';
import parseString from '../../../../../hooks/useLinkFormatter';
import appleControls from '../../../../../theme/icons/AppleControlButtons.png';
import { useEffect, useMemo, useState } from 'react';
import { PreviewData } from '../../../../../store/types/template';
import { PassNotification } from '../shared/PassNotification';
import BarcodePreview from '../BarcodePreview';
import {
  BackField,
  buildBackFields,
  filterBackFields,
  getApplePreviewNotificationSubtitle,
  getBackFieldHighlightStyles,
  getPreviewBarcodeDimensions,
  getPreviewBarcodeImage,
} from '../../utils/applePassPreview';
import { ApplePassField } from './ApplePassField';

const ApplePassBack = ({
  previewData,
  createTemplate,
  templatesPage,
  notifOpen,
}: {
  previewData: PreviewData;
  createTemplate?: boolean;
  templatesPage?: boolean;
  notifOpen: boolean;
}) => {
  const isBarcodeFieldHighlighted = useMemo(() => {
    return (
      previewData.isFieldHighlighted?.('barcode.enabled') ||
      previewData.isFieldHighlighted?.('barcode.type') ||
      previewData.isFieldHighlighted?.('barcode.source') ||
      previewData.isFieldHighlighted?.('barcode.value')
    );
  }, [previewData.isFieldHighlighted]);

  const [previewBarcodeImage, setPreviewBarcodeImage] = useState<string | null>(
    null
  );
  const [backFields, setBackFields] = useState<BackField[]>([]);
  const [filteredBackFields, setFilteredBackFields] = useState<BackField[]>([]);

  const { width: previewBarcodeImageWidth, height: previewBarcodeImageHeight } =
    useMemo(
      () => getPreviewBarcodeDimensions(previewData?.barcode?.type),
      [previewData?.barcode?.type]
    );

  useEffect(() => {
    setBackFields(buildBackFields(previewData));
  }, [previewData]);

  useEffect(() => {
    setFilteredBackFields(filterBackFields(previewData, backFields));
  }, [previewData, backFields]);

  useEffect(() => {
    const img = getPreviewBarcodeImage(previewData?.barcode?.type);
    setPreviewBarcodeImage(img);
  }, [previewData?.barcode?.type]);

  return (
    <>
      {/* NOTIFICATION */}
      <PassNotification
        open={notifOpen}
        autoHideMs={0}
        title={previewData?.notificationHeader || 'Notification header'}
        subtitle={getApplePreviewNotificationSubtitle(previewData)}
        imageUrl={previewData.appleIconLogo || previewData.iconLogo}
        inset="12.5% 7.5% auto 7%"
        device="apple"
      />

      {/* BACK CONTENT */}
      <Box
        position="absolute"
        inset={
          createTemplate &&
          previewBarcodeImage &&
          !!previewData.barcode.enabled &&
          !!previewData.barcode.type &&
          previewData.nfc.enabled === true
            ? '12% 6% 10% 5%'
            : '10% 6% 10% 5%'
        }
      >
        {createTemplate && (
          <>
            <Box
              borderTop="0px"
              maxW="full"
              m="0 auto"
              position="relative"
              zIndex={templatesPage && '-1'}
              p="15px"
              h="590px"
              overflowY="auto"
              sx={{
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {/* BARCODE */}
              {createTemplate &&
                previewBarcodeImage &&
                !!previewData.barcode.enabled &&
                !!previewData.barcode.type &&
                previewData.nfc.enabled === true && (
                  <>
                    <Flex w="full" justifyContent="center">
                      <Box
                        w="fit-content"
                        borderRadius="4px"
                        border={
                          isBarcodeFieldHighlighted
                            ? '2px solid #FF4935'
                            : 'none'
                        }
                        p={isBarcodeFieldHighlighted ? '4px' : '0'}
                      >
                        <BarcodePreview
                          type={previewData.barcode.type}
                          value={previewData.barcode.value}
                        />
                      </Box>
                    </Flex>

                    {/* BARCODE DISPLAY TEXT */}
                    {previewData.barcode.displayText &&
                      (() => {
                        const displayValue =
                          previewData.barcode.value !== ''
                            ? previewData.barcode.value
                            : previewData.barcode.source === 'custom'
                              ? ''
                              : previewData.barcode.source?.toUpperCase();
                        if (!displayValue) return null;
                        return (
                          <Flex w="full" justifyContent="center" mb={3} mt={2}>
                            <Box
                              w="fit-content"
                              borderRadius="4px"
                              border="none"
                              p="0"
                            >
                              <Text
                                fontSize="12px"
                                textStyle="headingFamilyRegularApplePay"
                              >
                                {displayValue}
                              </Text>
                            </Box>
                          </Flex>
                        );
                      })()}
                  </>
                )}

              {/* PASS DESCRIPTION */}
              <Flex pt="15px" justifyContent="center">
                <ApplePassField
                  value={previewData.description}
                  align="center"
                  fontSize="15px"
                  defaultText="Pass Description"
                  isHighlighted={previewData.isFieldHighlighted?.(
                    'description'
                  )}
                  p={
                    previewData.isFieldHighlighted?.('description')
                      ? '4px'
                      : '0'
                  }
                />
              </Flex>

              {/* CONTROL BUTTONS */}
              <Box>
                <Image py="15px" src={appleControls} alt="ios_buttons" />
              </Box>

              {/* BACK FIELDS */}
              {filteredBackFields.map((field, index) => {
                const { border, padding } = getBackFieldHighlightStyles(
                  field.name,
                  previewData.isFieldHighlighted
                );
                return (
                  <Box
                    key={index}
                    bg="white"
                    borderRadius={
                      filteredBackFields.length === 1
                        ? '10px'
                        : index === 0
                          ? '10px 10px 0px 0px'
                          : index === filteredBackFields.length - 1
                            ? '0px 0px 10px 10px'
                            : '0'
                    }
                    py="8px"
                    px="10px"
                    border="1px solid #E6E6E6"
                    borderTopWidth={index !== 0 ? '0' : '1px'}
                  >
                    <HStack
                      justify="space-between"
                      align="center"
                      border={border}
                      borderRadius="4px"
                      p={padding}
                      minH="28px"
                    >
                      <Text
                        fontSize="12px"
                        textStyle="headingFamilyRegularApplePay"
                        textAlign="left"
                        color="black"
                      >
                        {field.value}
                      </Text>
                      {(!!previewData[`${field.name}Id` as keyof PreviewData] ||
                        !!previewData[
                          `${field.name}Value` as keyof PreviewData
                        ]) && (
                        <Box textAlign="right" overflow="hidden">
                          {!!previewData[
                            `${field.name}Id` as keyof PreviewData
                          ] &&
                          !previewData[
                            `${field.name}Value` as keyof PreviewData
                          ] ? (
                            previewData.hideIdPlaceholder ? null : (
                            <Text
                              fontSize="13px"
                              textStyle="headingFamilyRegularApplePay"
                              textAlign="right"
                              color="applePayLabel"
                            >
                              {`<${
                                previewData[
                                  `${field.name}Id` as keyof PreviewData
                                ]
                              }>`}
                            </Text>
                            )
                          ) : (
                            (
                              previewData[
                                `${field.name}Value` as keyof PreviewData
                              ] as string
                            )
                              .split('\\n')
                              .map((i, key) => (
                                <Text
                                  fontSize="13px"
                                  textStyle="headingFamilyRegularApplePay"
                                  textAlign="right"
                                  color="applePayLabel"
                                  key={key}
                                >
                                  {parseString(i)}
                                </Text>
                              ))
                          )}
                        </Box>
                      )}
                    </HStack>
                  </Box>
                );
              })}

              {/* PASSENTRY TERMS */}
              <Box pt="30px">
                <Text
                  fontSize="12px"
                  textStyle="headingFamilyRegularApplePay"
                  textAlign="left"
                  color="applePayLabel"
                  pb="5px"
                >
                  PassEntry Terms
                </Text>
                <Text
                  fontSize="13px"
                  textStyle="headingFamilyRegularApplePay"
                  textAlign="left"
                  color="applePayLabel"
                  wordBreak="break-word"
                >
                  Your use of the Apple Wallet is subject to Apple's terms and
                  conditions of use, which PassEntry are not responsible for.
                </Text>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* BAR ICON */}
      <Flex
        position="absolute"
        inset="90% 10% auto 0"
        my={3}
        width="full"
        justifyContent="center"
        direction="row"
        align="center"
      >
        <Divider
          orientation="vertical"
          borderColor="black"
          w="25%"
          borderWidth="2px"
          borderRadius="25px"
        />
      </Flex>
    </>
  );
};

export default ApplePassBack;
